import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetAllClientMarketsQueryResponse, GetAllClientMarketsPathParams, GetAllClientMarketsQueryParams } from "../types/GetAllClientMarkets";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetAllClientMarketsClient = typeof client<GetAllClientMarketsQueryResponse, never, never>;
type GetAllClientMarkets = {
    data: GetAllClientMarketsQueryResponse;
    error: never;
    request: never;
    pathParams: GetAllClientMarketsPathParams;
    queryParams: GetAllClientMarketsQueryParams;
    headerParams: never;
    response: GetAllClientMarketsQueryResponse;
    client: {
        parameters: Partial<Parameters<GetAllClientMarketsClient>[0]>;
        return: Awaited<ReturnType<GetAllClientMarketsClient>>;
    };
};
export const getAllClientMarketsQueryKey = (clientCode: GetAllClientMarketsPathParams["clientCode"], params?: GetAllClientMarkets["queryParams"]) => [{ url: "/api/clients/:clientCode/markets", params: { clientCode: clientCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientMarketsQueryKey = ReturnType<typeof getAllClientMarketsQueryKey>;
export function getAllClientMarketsQueryOptions(clientCode: GetAllClientMarketsPathParams["clientCode"], params?: GetAllClientMarkets["queryParams"], options: GetAllClientMarkets["client"]["parameters"] = {}) {
    const queryKey = getAllClientMarketsQueryKey(clientCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientMarkets["data"], GetAllClientMarkets["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a list of all markets for a given client
 * @summary Get all
 * @link /api/clients/:clientCode/markets
 */
export function useGetAllClientMarkets<TData = GetAllClientMarkets["response"], TQueryData = GetAllClientMarkets["response"], TQueryKey extends QueryKey = GetAllClientMarketsQueryKey>(clientCode: GetAllClientMarketsPathParams["clientCode"], params?: GetAllClientMarkets["queryParams"], options: {
    query?: Partial<QueryObserverOptions<GetAllClientMarkets["response"], GetAllClientMarkets["error"], TData, TQueryData, TQueryKey>>;
    client?: GetAllClientMarkets["client"]["parameters"];
} = {}): UseQueryResult<TData, GetAllClientMarkets["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientMarketsQueryKey(clientCode, params);
    const query = useQuery({
        ...getAllClientMarketsQueryOptions(clientCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetAllClientMarkets["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getAllClientMarketsSuspenseQueryKey = (clientCode: GetAllClientMarketsPathParams["clientCode"], params?: GetAllClientMarkets["queryParams"]) => [{ url: "/api/clients/:clientCode/markets", params: { clientCode: clientCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientMarketsSuspenseQueryKey = ReturnType<typeof getAllClientMarketsSuspenseQueryKey>;
export function getAllClientMarketsSuspenseQueryOptions(clientCode: GetAllClientMarketsPathParams["clientCode"], params?: GetAllClientMarkets["queryParams"], options: GetAllClientMarkets["client"]["parameters"] = {}) {
    const queryKey = getAllClientMarketsSuspenseQueryKey(clientCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientMarkets["data"], GetAllClientMarkets["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a list of all markets for a given client
 * @summary Get all
 * @link /api/clients/:clientCode/markets
 */
export function useGetAllClientMarketsSuspense<TData = GetAllClientMarkets["response"], TQueryKey extends QueryKey = GetAllClientMarketsSuspenseQueryKey>(clientCode: GetAllClientMarketsPathParams["clientCode"], params?: GetAllClientMarkets["queryParams"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetAllClientMarkets["response"], GetAllClientMarkets["error"], TData, TQueryKey>>;
    client?: GetAllClientMarkets["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetAllClientMarkets["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientMarketsSuspenseQueryKey(clientCode, params);
    const query = useSuspenseQuery({
        ...getAllClientMarketsSuspenseQueryOptions(clientCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetAllClientMarkets["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}